import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useData } from "../backend/firebase";
import { Navigate } from 'react-router-dom';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { AVAILABLE_IMAGES, getImagePath } from '../utils/imageRegistry';
import ConfirmationModal from '../components/ConfirmationModal';
import { calculateVP, calculateBAM } from '../utils/scoringEngine';
import { parseLin, parseAliases } from '../utils/linParser';
import { DateTime } from 'luxon';

// --- FIX: Import & Register Resizer ---
import BlotFormatter from 'quill-blot-formatter';


// --- NEW: Import Table Module ---
import * as QuillTableUI from 'quill-table-ui';
import 'quill-table-ui/dist/index.css';

// Register the Table UI module


// --- NEW: Register Table Module ---


// --- FIX: Define Custom Image Blot to persist Styles ---
// This tells the editor: "Don't delete the style="..." attribute when saving!"
const BaseImage = Quill.import('formats/image');

class CustomImage extends BaseImage {
  static create(value) {
    const node = super.create(value);
    return node;
  }

  static formats(domNode) {
    // Allow specific attributes to be read from the DOM
    const map = {};
    if (domNode.hasAttribute('height')) map.height = domNode.getAttribute('height');
    if (domNode.hasAttribute('width')) map.width = domNode.getAttribute('width');
    if (domNode.hasAttribute('style')) map.style = domNode.getAttribute('style');
    return map;
  }

  format(name, value) {
    // Allow specific attributes to be written to the DOM
    if (name === 'height' || name === 'width') {
      if (value) {
        this.domNode.setAttribute(name, value);
      } else {
        this.domNode.removeAttribute(name);
      }
    } else if (name === 'style') {
      if (value) {
        this.domNode.setAttribute('style', value);
      } else {
        this.domNode.removeAttribute('style');
      }
    } else {
      super.format(name, value);
    }
  }
}

// Register the custom image logic
Quill.register('formats/image', CustomImage, true);
Quill.register('modules/blotFormatter', BlotFormatter);
Quill.register({'modules/tableUI': QuillTableUI.default}, true);
// --- EDITOR COMPONENT ---
const RenderEditor = ({ value, onChange }) => {
    const [editorBg, setEditorBg] = useState("#2D2B3F");
    const quillRef = useRef(null);

    // Image Handler: Prompts for URL
    const imageHandler = () => {
        const url = prompt("Paste the Image URL from the Media Tab:");
        if (url) {
            const quill = quillRef.current.getEditor();
            const range = quill.getSelection();
            // If selection is null (editor lost focus), insert at index 0
            const index = range ? range.index : 0;
            quill.insertEmbed(index, 'image', url);
        }
    };

    const tableHandler = () => {
        const quill = quillRef.current.getEditor();
        const table = quill.getModule('table');
        table.insertTable(3, 3); // Inserts a 3x3 table
    };

    // Memoize modules config
    const modules = useMemo(() => ({
        // 1. Image Resize Module
        blotFormatter: {
            overlay: {
                style: {
                    border: '2px solid #6C5DD3', // Visual feedback
                }
            }
        },
        table: true,      // Enable the native table module
        tableUI: true,    // Enable the context menu (Right-click/Tooltip)
        // 2. Toolbar
        toolbar: {
            container: [
                [{ 'font': [] }, { 'size': [] }],
                [{ 'header': [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [{ 'color': [] }, { 'background': [] }],
                [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
                [{ 'align': [] }],
                ['link', 'image', 'clean', 'table']
            ],
            handlers: {
                image: imageHandler,
                table: tableHandler 
            }
        }
    }), []);

    return (
      <div>
          <div style={{display:'flex', alignItems:'center', gap: 10, marginBottom: 5}}>
              <label style={{fontSize: 12, color: '#8F9EB5'}}>Editor Background:</label>
              <input 
                  type="color" 
                  value={editorBg} 
                  onChange={(e) => setEditorBg(e.target.value)} 
                  style={{width: 30, height: 30, border: 'none', cursor: 'pointer', background: 'transparent'}} 
                  title="Change background to see text better"
              />
              <button onClick={()=>setEditorBg("#ffffff")} style={{background:'white', border:'1px solid #ccc', fontSize:10, cursor:'pointer', color:'black', padding:'2px 6px', borderRadius:4}}>Light</button>
              <button onClick={()=>setEditorBg("#2D2B3F")} style={{background:'#2D2B3F', border:'1px solid #ccc', fontSize:10, cursor:'pointer', color:'white', padding:'2px 6px', borderRadius:4}}>Default (Dark)</button>
              <button onClick={()=>setEditorBg("#000000")} style={{background:'black', border:'1px solid #ccc', fontSize:10, cursor:'pointer', color:'white', padding:'2px 6px', borderRadius:4}}>Black</button>
          </div>
          {/* Use a wrapper div with relative positioning to help the overlay calculate position */}
          <div style={{color: editorBg === '#ffffff' ? 'black' : 'white', position: 'relative'}}>
              <ReactQuill 
                  ref={quillRef}
                  theme="snow" 
                  value={value} 
                  onChange={onChange} 
                  modules={modules} 
                  style={{backgroundColor: editorBg}} 
              />
          </div>
      </div>
    );
};

const Admin = () => {
  const { 
      userRole, allUsers, aliasesCsv, saveAliasesCsv,
      user, schedule, conditionsText, teams, standings, homeContent, navigation, donationsContent, customPages, globalStyle,
      addEvent, updateEvent, deleteEvent, updateConditions, saveTeams, saveStandings, saveHomeContent, saveNavigation, saveDonationsContent,
      addCustomPage, updateCustomPage, deleteCustomPage, saveGlobalStyle, updateUserRoleData, brackets, saveBrackets, themePresets, saveThemePresets
  } = useData();
  
  const [activeTab, setActiveTab] = useState("schedule");
  const [statusMsg, setStatusMsg] = useState(""); 

  const [schedTeamFilter, setSchedTeamFilter] = useState("");

  // ... [Keep ALL existing state variables (schedForm, teamDraft, etc...)] ...
  // (I am omitting the state declarations here to save space, but keep them exactly as they were in your previous file)
  const [schedEditingId, setSchedEditingId] = useState(null);
  const [schedForm, setSchedForm] = useState({ 
      team1: "", team2: "", startTime: "", endTime: "", links: [], description: "", result: "",
      stage: "RR", group: "", boardCount: 8, team1_score: "", team2_score: "", team1_bam: "", team2_bam: "",
      team1_vp: "", team2_vp: "", team1_penalty: 0, team2_penalty: 0, linData: "", isFinal: false, parsedBoards: [], parsedPlayers: [], linTeam1: "", linTeam2: ""
  });
  const [teamDraft, setTeamDraft] = useState({ name: "", color: "#ffffff", logo: "", logoUrl: "", bio: "", socialLink: "", rrGroup: "" });
  const [teamEditingId, setTeamEditingId] = useState(null);
  const [standingsDraft, setStandingsDraft] = useState([]); 
  const [rulesDraft, setRulesDraft] = useState("");
  const [homeDraft, setHomeDraft] = useState({ welcomeText: "", welcomeVideo: "" });
  const [donationsDraft, setDonationsDraft] = useState({ text: "", link: "", thankYouText: ""});
  const [navDraft, setNavDraft] = useState([]);
  const [styleDraft, setStyleDraft] = useState({ font: 'Inter', accentColor: '#6C5DD3', bgStart: '#7e6cd9', bgEnd: '#4a32e1', sidebarBg: '#110e22', cardBg: 'rgba(20, 20, 35, 0.6)' });
  const [pageEditingId, setPageEditingId] = useState(null);
  const [pageDraft, setPageDraft] = useState({ title: "", slug: "", content: "" });
  const [bracketDraft, setBracketDraft] = useState({ main: {}, consolation: {} });

  const [homeSections, setHomeSections] = useState([]);
  const [pageMeta, setPageMeta] = useState({ title: "", slug: "" }); // Meta data separate
  const [pageSections, setPageSections] = useState([]); // List of sections for current page
  const [modalState, setModalState] = useState({open: false, title: '', message: '', action: null });

    const [previewMode, setPreviewMode] = useState(false);
    const [newThemeName, setNewThemeName] = useState("");
    
    // Dynamically build the list of available themes
    // Dynamically build, deduplicate, and sort the themes
    const availableThemes = useMemo(() => {
        const defaultTheme = { id: 'default', name: 'Default Purple', font: 'Roboto', accentColor: '#6C5DD3', bgStart: '#7e6cd9', bgEnd: '#4a32e1', sidebarBg: '#110e22', cardBg: 'rgba(20, 20, 35, 0.6)' };
        
        // 1. Gather all potential sources into one raw array
        const rawThemes = [
            defaultTheme,
            ...(themePresets || []),
            globalStyle ? { ...globalStyle, id: 'global_active' } : null
        ].filter(Boolean);

        // 2. Deduplicate strictly by name (case-insensitive) to kill all repeats
        const uniqueMap = new Map();
        rawThemes.forEach(t => {
            const cleanName = (t.name || "Unnamed").trim();
            const key = cleanName.toLowerCase();
            // The first time a name is seen, it is saved. Duplicates are ignored.
            if (!uniqueMap.has(key)) {
                uniqueMap.set(key, { ...t, name: cleanName });
            }
        });

        const allUnique = Array.from(uniqueMap.values());

        // 3. Determine what is currently selected in your draft (fallback to global or default)
        const currentName = (styleDraft?.name || globalStyle?.name || "Default Purple").trim();

        // 4. Extract the currently selected theme
        const selectedTheme = allUnique.find(t => t.name.toLowerCase() === currentName.toLowerCase()) || allUnique[0];

        // 5. Filter out the selected theme from the remaining list, then sort alphabetically
        const otherThemes = allUnique
            .filter(t => t.name.toLowerCase() !== selectedTheme.name.toLowerCase())
            .sort((a, b) => a.name.localeCompare(b.name));

        // 6. Return the selected theme at index 0, followed by the alphabetical list
        return [selectedTheme, ...otherThemes];
    }, [themePresets, globalStyle, styleDraft?.name]);
  

  // --- SYNC DATA ON LOAD ---
  useEffect(() => {
    if(conditionsText && rulesDraft === "") setRulesDraft(conditionsText);
    if(standings.length > 0 && standingsDraft.length === 0) setStandingsDraft(standings);
    if(homeContent) setHomeDraft(homeContent);
    if(brackets) setBracketDraft(brackets);

    if(homeContent) {
        if (homeContent.sections) {
            setHomeSections(homeContent.sections);
        } else if (homeContent.welcomeText || homeContent.welcomeVideo) {
            // Convert old format to new sections format seamlessly
            const migrated = [];
            if(homeContent.welcomeVideo) migrated.push({ id: 1, type: 'media', content: homeContent.welcomeVideo, visible: true });
            if(homeContent.welcomeText) migrated.push({ id: 2, type: 'card', content: homeContent.welcomeText, visible: true });
            setHomeSections(migrated);
        }
    }

    if(donationsContent) setDonationsDraft(donationsContent);
    if(navigation.length > 0 && navDraft.length === 0) setNavDraft(navigation);
    if(globalStyle) setStyleDraft(prev => ({...prev, ...globalStyle}));
  }, [conditionsText, standings, homeContent, navigation, donationsContent, globalStyle]);

  if (!user) return <Navigate to="/login" />;
  if (!(userRole?.role === 'admin')) return <Navigate to="/captain" />;
  // --- HELPERS ---
  const addHomeSection = (type) => {
      setHomeSections([...homeSections, { id: Date.now(), type, content: "", visible: true }]);
  };
  
  const updateHomeSection = (index, field, value) => {
      const updated = [...homeSections];
      updated[index][field] = value;
      setHomeSections(updated);
  };

  const moveHomeSection = (index, direction) => {
      const updated = [...homeSections];
      if (direction === -1 && index > 0) {
          [updated[index], updated[index - 1]] = [updated[index - 1], updated[index]];
      } else if (direction === 1 && index < updated.length - 1) {
          [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
      }
      setHomeSections(updated);
  };

  const removeHomeSection = (index) => {
      if(!window.confirm("Delete this section?")) return;
      setHomeSections(homeSections.filter((_, i) => i !== index));
  };

  const handleSaveHome = async () => {
      // Save as 'sections' property in the home settings doc
      await saveWrapper(saveHomeContent, { sections: homeSections }, "Home Page Updated");
  };

  const confirmAction = (title, message, actionToPerform) => {
        setModalState({
            open: true,
            title: title,
            message: message,
            action: actionToPerform
        });
    };

  const addPageSection = (type) => setPageSections([...pageSections, { id: Date.now(), type, content: "", visible: true }]);
  const updatePageSection = (index, field, value) => { const u = [...pageSections]; u[index][field] = value; setPageSections(u); };
  const movePageSection = (index, dir) => { const u = [...pageSections]; if (dir===-1 && index>0) {[u[index], u[index-1]]=[u[index-1], u[index]]} else if(dir===1 && index<u.length-1) {[u[index], u[index+1]]=[u[index+1], u[index]]} setPageSections(u); };
  const removePageSection = (i) => { if(window.confirm("Delete section?")) setPageSections(pageSections.filter((_,x)=>x!==i)); };

  const handleEditPage = (p) => { 
      setPageEditingId(p.id); 
      
      // 1. Populate Title & Slug
      setPageDraft({ 
          title: p.title || "", 
          slug: p.slug || "" 
      });
      
      // 2. Populate Sections (The Fix)
      if (p.sections && Array.isArray(p.sections) && p.sections.length > 0) {
          // Load existing builder sections
          setPageSections(p.sections);
      } else if (p.content) {
          // LEGACY MIGRATION: If page has old text content but no sections, 
          // wrap it in a Glass Card so it appears in the builder.
          setPageSections([{
              id: Date.now(),
              type: 'card',
              content: p.content,
              visible: true
          }]);
      } else {
          // Empty page
          setPageSections([]);
      }
  };
  
  const handleSavePage = async () => {
      // 1. Debugging: Check console to see what is about to be saved
      console.log("Saving Page Payload:", pageDraft);

      // 2. Validation
      if(!pageDraft.title) return alert("Page Title is required");
      if(!pageDraft.slug && !pageEditingId) return alert("URL Slug is required");

      // 3. Prepare Slug
      // If editing, keep original slug. If new, sanitize the input.
      const cleanSlug = pageEditingId 
          ? pageDraft.slug 
          : pageDraft.slug.toLowerCase().trim().replace(/ /g, '-').replace(/[^\w-]+/g, '');

      // 4. Construct Payload
      const payload = { 
          title: pageDraft.title,
          slug: cleanSlug,
          content: pageDraft.content || "", // Legacy support
          sections: pageSections // <--- ADD THIS LINE
      };
      
      // 5. Database Operations
      if(pageEditingId) {
          await updateCustomPage(pageEditingId, payload);
      } else { 
          await addCustomPage(payload); 
          
          // Add to Navigation (Hidden by default)
          const link = `/page/${cleanSlug}`;
          const exists = navDraft.find(n => n.link === link);
          if(!exists) {
              const newNav = [...navDraft, { title: payload.title, link: link, visible: false, type: 'custom' }];
              setNavDraft(newNav); 
              await saveNavigation(newNav); 
          }
      }
      
      // 6. Reset Form
      setPageEditingId(null); 
      setPageDraft({ title: "", slug: "", content: "" });
      setPageSections([]); // <--- ADD THIS LINE TO CLEAR EDITOR
      
      setStatusMsg("✅ Page Saved"); 
      setTimeout(()=>setStatusMsg(""), 2000);
  };

  // --- UPDATED DELETE PAGE HANDLER ---
  const handleDeletePage = async (id) => { 
      if(!window.confirm("Delete this page? This cannot be undone.")) return; 
      
      // 1. Find page info BEFORE deletion
      const p = customPages.find(x => x.id === id); 
      
      // 2. Delete from Pages Collection
      await deleteCustomPage(id); 
      
      // 3. Update Navigation
      if(p) {
          const linkToRemove = `/page/${p.slug}`;
          const newNav = navDraft.filter(n => n.link !== linkToRemove);
          setNavDraft(newNav); // Update local state immediately
          await saveNavigation(newNav); // Sync to DB
      }
      
      setStatusMsg("✅ Page Deleted"); setTimeout(()=>setStatusMsg(""), 2000);
  };

  const saveWrapper = async (fn, arg, msg="Saved!") => {
      setStatusMsg("Saving...");
      try {
          await fn(arg); // Wait for the Firebase function to finish
          setStatusMsg("✅ " + msg); 
          setTimeout(() => setStatusMsg(""), 2000); 
      } catch (error) {
          console.error(error);
          setStatusMsg("❌ Error"); 
      }
  };


  // --- UPDATED PREVIEW HELPER ---
  // This handles both SVGs (strings) and standard images (filenames)
  const renderLogoPreview = (teamData) => {
      // 1. Priority: Uploaded SVG
      if (teamData.logo) {
          const dataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(teamData.logo)}`;
          return <img src={dataUrl} alt="preview" style={{width: 40, height: 40, objectFit: 'contain', background: 'rgba(255,255,255,0.1)', borderRadius: 4, padding: 2}} />;
      }
      // 2. Secondary: Selected Media Image
      if (teamData.logoUrl) {
          return <img src={getImagePath(teamData.logoUrl)} alt="preview" style={{width: 40, height: 40, objectFit: 'contain', background: 'rgba(255,255,255,0.1)', borderRadius: 4, padding: 2}} />;
      }
      // 3. Fallback: Color Box
      return <div style={{width: 40, height: 40, background: teamData.color || '#333', borderRadius: 4, border: '1px solid #555'}}></div>;
  };

  // --- NEW: MEDIA HELPER ---
  const copyToClipboard = (text, msg) => {
      navigator.clipboard.writeText(text);
      setStatusMsg(`✅ ${msg}`);
      setTimeout(() => setStatusMsg(""), 2000);
  };

  // ... [Keep all other handlers: Teams, Schedule, Standings, Custom Pages, Nav, Users] ...
  // (I am assuming you kept the handlers from the previous full code block)
  const handleLogoFileChange = (e) => { const f=e.target.files[0]; if(!f)return; const r=new FileReader(); r.onload=(ev)=>setTeamDraft(p=>({...p, logo:ev.target.result})); r.readAsText(f); };
  const deleteSched = (id) => { if(window.confirm("Delete?")) deleteEvent(id); };
  
  const recalculateFromBoards = (formState) => {
      let imps1 = 0, imps2 = 0, mainCount = 0;
      let beer1 = 0, beer2 = 0; // NEW: Track Beer Points
      let bam1 = 0, bam2 = 0;   // NEW: Track BAM from parser
      let activeRawMargins = []; 

      // 1. If we have a LIN file, sum the boards
      if (formState.parsedBoards && formState.parsedBoards.length > 0) {
          formState.parsedBoards.forEach(b => {
              if (b.segment === 'Main') {
                  mainCount++;
              }
              
              // KO FIX: Sum IMPs, BAM, and Beer for ALL played boards (Main + Tiebreaks)!
              if (b.segment !== 'Omitted') {
                  imps1 += b.imps1 || 0;
                  imps2 += b.imps2 || 0;
                  activeRawMargins.push(b.rawMargin || 0);

                  // Accumulate tiebreakers
                  beer1 += b.beer1 || 0;
                  beer2 += b.beer2 || 0;
                  bam1 += b.bam1 || 0;
                  bam2 += b.bam2 || 0;
              }
          });
      } else {
          // 2. MANUAL ENTRY MODE: Use the numbers typed into the form
          imps1 = parseFloat(formState.team1_score) || 0;
          imps2 = parseFloat(formState.team2_score) || 0;
          beer1 = parseFloat(formState.team1_beer) || 0;
          beer2 = parseFloat(formState.team2_beer) || 0;
          bam1 = parseFloat(formState.team1_bam) || 0;
          bam2 = parseFloat(formState.team2_bam) || 0;
          activeRawMargins = null; // Signal to NOT overwrite manual BAM/Beer entries
      }

      // VP is still calculated based on the Main segment board count
      const activeBoardCount = mainCount > 0 ? mainCount : formState.boardCount;
      const { vp1, vp2 } = calculateVP(imps1, imps2, activeBoardCount, formState.team1_penalty, formState.team2_penalty);
      
      const nextState = {
          ...formState,
          team1_score: imps1,
          team2_score: imps2,
          team1_vp: vp1,
          team2_vp: vp2,
          team1_beer: beer1, // Inject into form state
          team2_beer: beer2, // Inject into form state
          boardCount: activeBoardCount
      };

      // Only auto-update BAM if we are pulling from a LIN file
      if (activeRawMargins !== null) {
          // Bypass the old calculateBAM helper and use the new per-board parser math!
          nextState.team1_bam = bam1;
          nextState.team2_bam = bam2;
      }

      return nextState;
  };

  // Helper for manual VP calculation (if no LIN is used)
  const handleAutoCalcVP = () => {
      setSchedForm(prev => {
          // Force calculation based strictly on the text inputs, ignoring the LIN array
          const imps1 = parseFloat(prev.team1_score) || 0;
          const imps2 = parseFloat(prev.team2_score) || 0;
          
          const { vp1, vp2 } = calculateVP(
              imps1, 
              imps2, 
              prev.boardCount, 
              prev.team1_penalty, 
              prev.team2_penalty
          );
          
          return { 
              ...prev, 
              team1_vp: vp1, 
              team2_vp: vp2 
          };
      });
  };

  const handleSwapAllBoards = () => {
      setSchedForm(prev => {
          const updatedBoards = prev.parsedBoards.map(b => {
              const updated = {
                  ...b,
                  imps1: b.imps2 ?? 0,
                  imps2: b.imps1 ?? 0,
                  beer1: b.beer2 ?? 0, // NEW: Swap Beer
                  beer2: b.beer1 ?? 0, // NEW: Swap Beer
                  bam1: b.bam2 ?? 0,   // NEW: Swap BAM
                  bam2: b.bam1 ?? 0,   // NEW: Swap BAM
                  room1: b.room2 ?? "",
                  room2: b.room1 ?? "",
                  raw1: b.raw2 ?? 0,
                  raw2: b.raw1 ?? 0,
                  rawMargin: -(b.rawMargin || 0)
              };

              if (b.score1 !== undefined || b.score2 !== undefined) {
                  updated.score1 = b.score2 ?? 0;
                  updated.score2 = b.score1 ?? 0;
              }

              if (b.details) {
                  updated.details = {
                      o: b.details.c || null,
                      c: b.details.o || null
                  };
              }

              Object.keys(updated).forEach(key => {
                  if (updated[key] === undefined) delete updated[key];
              });

              return updated;
          });

          return recalculateFromBoards({ 
              ...prev, 
              parsedBoards: updatedBoards,
              isAllSwapped: !prev.isAllSwapped 
          });
      });
  };

  // The Main LIN Processor
  const handleProcessLin = () => {
      if (!schedForm.linData) return alert("Please paste LIN data first.");
      try {
          const aliasMap = parseAliases(aliasesCsv);
          const parsed = parseLin(schedForm.linData, aliasMap);
          
          // Auto-assign segments (first X boards = Main, rest = TB1)
          const boards = parsed.boards.map((b, i) => ({
              ...b,
              segment: b.isOmitted ? 'Omitted' : (i < schedForm.boardCount ? 'Main' : 'TB1')
          }));
          
          setSchedForm(prev => {
              const next = { 
                  ...prev, 
                  parsedBoards: boards, 
                  parsedPlayers: parsed.players, 
                  linTeam1: parsed.team1Name, 
                  linTeam2: parsed.team2Name,
                  isAllSwapped: false // Reset toggle on new upload
              };
              return recalculateFromBoards(next);
          });
          
          setStatusMsg("✅ LIN Processed");
          setTimeout(() => setStatusMsg(""), 2000);
      } catch (err) {
          alert("Failed to parse LIN: " + err.message);
      }
  };

  const handleSwapLinTeams = () => {
      if (!schedForm.parsedBoards || schedForm.parsedBoards.length === 0) return;

      setSchedForm(prev => {
          // 1. Swap IMPs and invert the raw margin for every board
          const swappedBoards = prev.parsedBoards.map(b => ({
              ...b,
              imps1: b.imps2,
              imps2: b.imps1,
              rawMargin: -(b.rawMargin || 0)
          }));

          // 2. FIX: Swap the players between Open and Closed rooms to preserve Rosters!
          const p = [...prev.parsedPlayers];
          if (p.length >= 8) {
              [p[0], p[4]] = [p[4], p[0]]; // Swap Souths
              [p[1], p[5]] = [p[5], p[1]]; // Swap Wests
              [p[2], p[6]] = [p[6], p[2]]; // Swap Norths
              [p[3], p[7]] = [p[7], p[3]]; // Swap Easts
          }

          const next = {
              ...prev,
              parsedBoards: swappedBoards,
              linTeam1: prev.linTeam2, // Swap the parsed LIN names
              linTeam2: prev.linTeam1,
              parsedPlayers: p         // Inject the successfully swapped rosters!
          };

          return recalculateFromBoards(next); 
      });
  };

  // Handles dropdown changes for individual boards
  const handleBoardSegmentChange = (idx, newSegment) => {
      setSchedForm(prev => {
          const updatedBoards = [...prev.parsedBoards];
          updatedBoards[idx].segment = newSegment;
          return recalculateFromBoards({ ...prev, parsedBoards: updatedBoards });
      });
  };

//   const handleSwapSingleBoard = (idx) => {
//       setSchedForm(prev => {
//           const updatedBoards = [...prev.parsedBoards];
//           const b = updatedBoards[idx];
          
//           // 1. Perform the swap with strict fallbacks to prevent 'undefined'
//           updatedBoards[idx] = {
//               ...b,
//               imps1: b.imps2 ?? 0,
//               imps2: b.imps1 ?? 0,
//               room1: b.room2 ?? "",
//               room2: b.room1 ?? "",
//               raw1: b.raw2 ?? 0,
//               raw2: b.raw1 ?? 0,
//               rawMargin: -(b.rawMargin || 0)
//           };

//           // 2. Safely swap legacy scores ONLY if they exist
//           if (b.score1 !== undefined || b.score2 !== undefined) {
//               updatedBoards[idx].score1 = b.score2 ?? 0;
//               updatedBoards[idx].score2 = b.score1 ?? 0;
//           }

//           // 3. Swap the underlying hand/bidding data so the HandAnalyzer matches!
//           if (b.details) {
//               updatedBoards[idx].details = {
//                   o: b.details.c || null,
//                   c: b.details.o || null
//               };
//           }

//           // 4. FIREBASE SAFETY SCRUB: Delete any accidental undefined properties
//           Object.keys(updatedBoards[idx]).forEach(key => {
//               if (updatedBoards[idx][key] === undefined) {
//                   delete updatedBoards[idx][key];
//               }
//           });

//           return recalculateFromBoards({ ...prev, parsedBoards: updatedBoards });
//       });
//   };

  const handleTeamSubmit = async () => { const t = { ...teamDraft, id: teamEditingId || Date.now() }; const list = teamEditingId ? teams.map(x=>x.id===teamEditingId?t:x) : [...teams, t]; saveWrapper(saveTeams, list, "Team Saved"); setTeamDraft({name:"", color:"#fff", logo:"", logoUrl: "", bio: "", socialLink: "", rrGroup: ""}); setTeamEditingId(null); document.getElementById('logo-upload').value="";};
  const startEditTeam = (t) => { setTeamEditingId(t.id); setTeamDraft({ socialLink: "", rrGroup: "", ...t }); }; // Safe merge
  const cancelEditTeam = () => { setTeamEditingId(null); setTeamDraft({ name: "", color: "#ffffff", logo: "", logoUrl: "", bio: "", socialLink: "", rrGroup: "" }); };
  const handleDeleteTeam = (id) => { if(window.confirm("Remove?")) saveWrapper(saveTeams, teams.filter(t=>t.id!==id), "Team Removed"); };
  const handleAddLink = () => setSchedForm({ ...schedForm, links: [...schedForm.links, { url: "", label: "", teamId: "" }] });
  const handleLinkChange = (i, f, v) => { const l = [...schedForm.links]; if(typeof l[i]==='string') l[i]={url:l[i],label:"",teamId:""}; l[i] = {...l[i], [f]:v}; setSchedForm({...schedForm, links:l}); };
  const handleRemoveLink = (i) => setSchedForm({...schedForm, links: schedForm.links.filter((_,x)=>x!==i)});
  const handleSchedSubmit = async () => { 
        const l = schedForm.links.filter(x=>x.url&&x.url.trim()!==""); 
        let title = schedForm.title || "Bridge Match"; 
        if(schedForm.team1 && schedForm.team2) { 
            const t1 = teams.find(t => t.id.toString() === schedForm.team1.toString()); 
            const t2 = teams.find(t => t.id.toString() === schedForm.team2.toString()); 
            title = `${t1?.name || "TBA"} vs ${t2?.name || "TBA"}`; 
        } 

        // FICTITIOUS RESULT FIX: Auto-calculate VPs if manually entered but the green button was forgotten
        let finalVp1 = schedForm.team1_vp;
        let finalVp2 = schedForm.team2_vp;
        if ((schedForm.team1_score || schedForm.team2_score) && !finalVp1 && !finalVp2) {
            const { vp1, vp2 } = calculateVP(parseFloat(schedForm.team1_score)||0, parseFloat(schedForm.team2_score)||0, schedForm.boardCount || 8, schedForm.team1_penalty, schedForm.team2_penalty);
            finalVp1 = vp1; finalVp2 = vp2;
        }

        const p = {...schedForm, title, links:l, team1_vp: finalVp1, team2_vp: finalVp2}; 
        
        // CLONE FIX: Strip the internal database keys from the payload so they don't overwrite the Firestore keys!
        delete p.id;
        delete p.chunkId;

        if(schedEditingId) await updateEvent(schedEditingId, p); 
        else await addEvent(p); 
        
        setSchedEditingId(null); 
        // NEW: Ensure all tiebreaker fields reset properly
        setSchedForm({ team1:"", team2:"", startTime:"", endTime:"", links:[], description:"", result:"", stage: "RR", group: "", boardCount: 8, team1_score: "", team2_score: "", team1_bam: "", team2_bam: "", team1_beer: "", team2_beer: "", winner: "", team1_vp: "", team2_vp: "", team1_penalty: 0, team2_penalty: 0, linData: "", isFinal: false, parsedBoards: [], parsedPlayers: [], linTeam1: "", linTeam2: "" }); 
        saveWrapper(async()=>true, null, "Match Saved"); 
    };

    const startEditSched = (i) => { 
        setSchedEditingId(i.id); 
        let l = i.links || (i.link ? [{url:i.link,label:"",teamId:""}] : []); 
        setSchedForm({
            stage: "RR", group: "", boardCount: 8, team1_score: "", team2_score: "", 
            team1_bam: "", team2_bam: "", team1_beer: "", team2_beer: "", winner: "", 
            team1_vp: "", team2_vp: "", team1_penalty: 0, team2_penalty: 0, linData: "", isFinal: false, parsedBoards: [], parsedPlayers: [], linTeam1: "", linTeam2: "" ,  
            ...i, links:l, result: i.result||""
        }); 
    };

    const cancelEditSched = () => { 
        setSchedEditingId(null); 
        setSchedForm({ team1:"", team2:"", startTime:"", endTime:"", links:[], description:"", result:"", stage: "RR", group: "", boardCount: 8, team1_score: "", team2_score: "", team1_bam: "", team2_bam: "", team1_beer: "", team2_beer: "", winner: "", team1_vp: "", team2_vp: "", team1_penalty: 0, team2_penalty: 0, linData: "", isFinal: false, parsedBoards: [], parsedPlayers: [], linTeam1: "", linTeam2: "" }); 
    };

  const moveLinkUp = (idx) => { if(idx===0)return; const l=[...schedForm.links]; [l[idx],l[idx-1]]=[l[idx-1],l[idx]]; setSchedForm({...schedForm, links:l}); };
  const moveLinkDown = (idx) => { if(idx===schedForm.links.length-1)return; const l=[...schedForm.links]; [l[idx],l[idx+1]]=[l[idx+1],l[idx]]; setSchedForm({...schedForm, links:l}); };
  //   const handleSavePage = async () => { if(!pageDraft.title || !pageDraft.slug) return alert("Title and Slug required"); const cleanSlug = pageDraft.slug.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, ''); const payload = { ...pageDraft, slug: cleanSlug }; if(pageEditingId) await updateCustomPage(pageEditingId, payload); else { await addCustomPage(payload); const exists = navDraft.find(n => n.link === `/page/${cleanSlug}`); if(!exists) await saveNavigation([...navDraft, { title: payload.title, link: `/page/${cleanSlug}`, visible: true, type: 'custom' }]); } setPageEditingId(null); setPageDraft({ title: "", slug: "", content: "" }); setStatusMsg("✅ Page Saved"); setTimeout(()=>setStatusMsg(""), 2000); };
//   const handleEditPage = (p) => { setPageEditingId(p.id); setPageDraft(p); };
//   const handleDeletePage = async (id) => { if(!window.confirm("Delete?")) return; await deleteCustomPage(id); const p = customPages.find(x => x.id === id); if(p) await saveNavigation(navDraft.filter(n => n.link !== `/page/${p.slug}`)); };
  const moveNav = (idx, dir) => { const n = [...navDraft]; if(dir===-1&&idx>0){[n[idx],n[idx-1]]=[n[idx-1],n[idx]]} else if(dir===1&&idx<n.length-1){[n[idx],n[idx+1]]=[n[idx+1],n[idx]]} setNavDraft(n); };
  const handleUserUpdate = async (uid, field, value) => { await updateUserRoleData(uid, { [field]: value }); };

  const isAdmin = userRole?.role === 'admin';

  const PreviewStyles = () => {
        if (!previewMode) return null;
        return (
            <style>{`
                /* Target strictly the PageLayout to prevent stacked gradient compounding */
                :root {
                    --accent-color: ${styleDraft.accentColor} !important;
                    --sidebar-bg: ${styleDraft.sidebarBg} !important;
                    --bg-gradient-start: ${styleDraft.bgStart} !important;
                    --bg-gradient-end: ${styleDraft.bgEnd} !important;
                }
                
                /* Target your specific Admin Sidebar class */
                .PrimaryButton { background: ${styleDraft.accentColor} !important; }
                
                /* Force links and accent text to use the preview color */
                a, .AccentText { color: ${styleDraft.accentColor} !important; }
            `}</style>
        );
    };


  return (
    <div className="PageLayout">
        <PreviewStyles />
        <div className="ContentWrapper">
            
            <div className="GlassCard" style={{padding: 0, display:'flex', flexDirection:'column'}}>
                
                <div className="GlassHeader" style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <h1 className="SectionTitle" style={{margin: 0, fontSize: 24}}>
                        {isAdmin ? "Admin Dashboard" : "Captain's Quarters"}
                    </h1>
                    {statusMsg && <span style={{color: statusMsg.includes('Error')?'#FF5C5C':'#26A65B', fontWeight: 'bold'}}>{statusMsg}</span>}
                </div>

                <div className="AdminContainer" style={{padding: 20}}>
                    
                    {/* --- SIDEBAR --- */}
                    <div className="AdminSidebar">
                        {isAdmin ? (
                            <>
                                <button className={activeTab==='schedule'?'active':''} onClick={()=>setActiveTab('schedule')}>📅 Schedule</button>
                                <button className={activeTab==='pages'?'active':''} onClick={()=>setActiveTab('pages')}>📄 Custom Pages</button>
                                <button className={activeTab==='teams'?'active':''} onClick={()=>setActiveTab('teams')}>🛡️ Teams</button>
                                <button className={activeTab==='users'?'active':''} onClick={()=>setActiveTab('users')}>👥 Users</button>
                                <button className={activeTab==='media'?'active':''} onClick={()=>setActiveTab('media')}>🖼️ Media</button> {/* NEW TAB */}
                                <div style={{height:1, background:'rgba(255,255,255,0.1)', margin:'5px 0'}}></div>
                                <button className={activeTab==='home'?'active':''} onClick={()=>setActiveTab('home')}>🏠 Home</button>
                                <button className={activeTab==='donate'?'active':''} onClick={()=>setActiveTab('donate')}>💰 Donations</button>
                                <button className={activeTab==='rules'?'active':''} onClick={()=>setActiveTab('rules')}>📜 Rules</button>
                                <button className={activeTab==='nav'?'active':''} onClick={()=>setActiveTab('nav')}>🔗 Navigation</button>
                                <button className={activeTab==='style'?'active':''} onClick={()=>setActiveTab('style')}>🎨 Style</button>
                            </>
                        ) : (
                            <button className="active">🔒 Locked</button>
                        )}
                    </div>

                    <div className="AdminMain ScrollArea">
                        {/* === MEDIA TAB (New) === */}
                        {activeTab === 'media' && (
                            <div>
                                <div style={{background:'rgba(255,255,255,0.05)', padding: 15, borderRadius: 8, marginBottom: 20}}>
                                    <h4 style={{margin: '0 0 10px 0'}}>How to add images:</h4>
                                    <ol style={{fontSize: 13, color: '#ccc', paddingLeft: 20, lineHeight: 1.5}}>
                                        <li>Add your image file (.png or .jpg) to the <code>public/assets/images/</code> folder in your project code.</li>
                                        <li>Open <code>src/utils/imageRegistry.js</code> and add an entry to the list.</li>
                                        <li>Rebuild/Restart the site. The image will appear below.</li>
                                    </ol>
                                </div>

                                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 20}}>
                                    {AVAILABLE_IMAGES.map((img, idx) => (
                                        <div key={idx} style={{background: 'rgba(0,0,0,0.3)', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)'}}>
                                            <div style={{height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#111'}}>
                                                <img src={getImagePath(img.filename)} alt={img.name} style={{maxHeight: '100%', maxWidth: '100%', objectFit: 'contain'}} />
                                            </div>
                                            <div style={{padding: 10}}>
                                                <div style={{fontWeight: 'bold', fontSize: 13, marginBottom: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{img.name}</div>
                                                <div style={{display: 'flex', flexDirection: 'column', gap: 5}}>
                                                    <button 
                                                        onClick={() => copyToClipboard(getImagePath(img.filename), "URL Copied")} 
                                                        style={{background: '#333', border: 'none', color: 'white', fontSize: 11, padding: '4px', borderRadius: 4, cursor: 'pointer'}}
                                                    >
                                                        Copy URL
                                                    </button>
                                                    <button 
                                                        onClick={() => copyToClipboard(`<img src="${getImagePath(img.filename)}" alt="${img.name}" style="width: 100%; max-width: 400px;" />`, "Embed Code Copied")} 
                                                        style={{background: '#6C5DD3', border: 'none', color: 'white', fontSize: 11, padding: '4px', borderRadius: 4, cursor: 'pointer'}}
                                                    >
                                                        Copy Embed Code
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* === CAPTAIN VIEW === */}
                        {!isAdmin && (
                            <div style={{textAlign:'center', padding: 50, opacity: 0.7}}>
                                <h2>Welcome, Captain!</h2>
                                <p>Your team management tools will appear here soon.</p>
                                {userRole?.teamId ? (
                                    <p style={{color: '#6C5DD3'}}>
                                        Assigned Team: <strong>{teams.find(t=>t.id.toString()===userRole.teamId)?.name || "Unknown"}</strong>
                                    </p>
                                ) : (
                                    <p style={{color: '#FF5C5C'}}>No team assigned yet.</p>
                                )}
                            </div>
                        )}

                        {/* ... [INCLUDE ALL OTHER TABS HERE: Schedule, Teams, Pages, etc.] ... */}
                        {/* (Paste the rest of the tabs from the previous full Admin.js) */}
                        
                        {/* === RULES TAB === */}
                        {activeTab === 'rules' && (
                            <div>
                                <div style={{display:'flex', justifyContent:'space-between', marginBottom:10}}>
                                    <h3>Conditions of Contest</h3>
                                    <button className="PrimaryButton" onClick={()=>saveWrapper(updateConditions, rulesDraft, "Rules Saved")}>Save Changes</button>
                                </div>
                                <RenderEditor value={rulesDraft} onChange={setRulesDraft} />
                            </div>
                        )}
                        {/* ... etc ... */}
                         {/* === HOME TAB === */}
                        {activeTab === 'home' && (
                            <div>
                                <div style={{display:'flex', justifyContent:'space-between', marginBottom: 10}}>
                                    <h3>Home Page Layout</h3>
                                    <button className="PrimaryButton" onClick={handleSaveHome}>Save Layout</button>
                                </div>

                                {/* Add Section Buttons */}
                                <div style={{display:'flex', gap: 10, marginBottom: 20, background: 'rgba(0,0,0,0.2)', padding: 10, borderRadius: 8}}>
                                    <button onClick={()=>addHomeSection('card')} style={{background: '#6C5DD3', border:'none', color:'white', padding:'8px 12px', borderRadius:6, cursor:'pointer'}}>+ Add Glass Card (Text/Image)</button>
                                    <button onClick={()=>addHomeSection('carousel')} style={{background: '#F39C12', border:'none', color:'white', padding:'8px 12px', borderRadius:6, cursor:'pointer'}}>+ Add Bio Carousel</button>
                                    <button onClick={()=>addHomeSection('media')} style={{background: '#26A65B', border:'none', color:'white', padding:'8px 12px', borderRadius:6, cursor:'pointer'}}>+ Add Standalone Video/Image</button>
                                </div>

                                {/* Sections List */}
                                {homeSections.map((section, index) => (
                                    <div key={section.id} style={{background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: 15, marginBottom: 15, border: '1px solid rgba(255,255,255,0.1)'}}>
                                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 10}}>
                                            <div style={{fontWeight: 'bold', color: section.type==='card' ? '#6C5DD3' : section.type==='carousel' ? '#F39C12' : '#26A65B'}}>
                                                {section.type === 'card' ? "📄 Glass Card (Text)" : section.type === 'carousel' ? "🎠 Bio Carousel" : "🎬 Standalone Media"}
                                            </div>
                                            <div style={{display:'flex', gap: 5}}>
                                                <button onClick={()=>moveHomeSection(index, -1)} disabled={index===0} style={{cursor:'pointer', background:'transparent', border:'none', color:'white'}}>▲</button>
                                                <button onClick={()=>moveHomeSection(index, 1)} disabled={index===homeSections.length-1} style={{cursor:'pointer', background:'transparent', border:'none', color:'white'}}>▼</button>
                                                <input type="checkbox" checked={section.visible} onChange={(e)=>updateHomeSection(index, 'visible', e.target.checked)} style={{cursor:'pointer', width:20, height:20}} title="Visible?" />
                                                <button onClick={()=>removeHomeSection(index)} style={{color:'#FF5C5C', background:'transparent', border:'none', cursor:'pointer', marginLeft: 10}}>Delete</button>
                                            </div>
                                        </div>

                                        {section.type === 'card' && (
                                            <RenderEditor value={section.content} onChange={(val) => updateHomeSection(index, 'content', val)} />
                                        )}

                                        {section.type === 'media' && (
                                            <div>
                                                <label style={{color: '#8F9EB5', fontSize: 12}}>Image URL or YouTube/Twitch Link</label>
                                                <input 
                                                    className="DarkInput" 
                                                    value={section.content} 
                                                    onChange={(e) => updateHomeSection(index, 'content', e.target.value)}
                                                    placeholder="https://..."
                                                />
                                                <div style={{fontSize: 11, color: '#555', marginTop: 5}}>
                                                    * Images will center automatically. Videos will auto-embed.
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                
                                <div style={{textAlign:'right', marginTop: 20}}><button className="PrimaryButton" onClick={handleSaveHome}>Save Layout</button></div>
                            </div>
                        )}

                        {/* === DONATE TAB === */}
                        {activeTab === 'donate' && (
                            <div>
                                <div style={{display:'flex', justifyContent:'space-between', marginBottom:10}}>
                                    <h3>Donations Page</h3>
                                    <button className="PrimaryButton" onClick={()=>saveWrapper(saveDonationsContent, donationsDraft, "Saved")}>Save Changes</button>
                                </div>
                                {/* NEW: Prize Pool Inputs */}
                                <div style={{display:'flex', gap: 15, marginBottom: 20, background: 'rgba(0,0,0,0.2)', padding: 15, borderRadius: 8}}>
                                    <div style={{flex: 1}}>
                                        <label style={{color: '#8F9EB5', fontSize: 12}}>Raised Amount ($)</label>
                                        <input 
                                            type="number" 
                                            className="DarkInput" 
                                            style={{marginBottom: 0}}
                                            value={donationsDraft.raised || ""} 
                                            onChange={(e) => setDonationsDraft({...donationsDraft, raised: e.target.value})} 
                                            placeholder="e.g. 2150"
                                        />
                                    </div>
                                    <div style={{flex: 1}}>
                                        <label style={{color: '#8F9EB5', fontSize: 12}}>Goal Amount ($)</label>
                                        <input 
                                            type="number" 
                                            className="DarkInput" 
                                            style={{marginBottom: 0}}
                                            value={donationsDraft.goal || ""} 
                                            onChange={(e) => setDonationsDraft({...donationsDraft, goal: e.target.value})} 
                                            placeholder="e.g. 5000"
                                        />
                                    </div>
                                </div>

                                <label style={{color: '#8F9EB5', fontSize: 12}}>Payment Link</label>
                                <input className="DarkInput" value={donationsDraft.link} onChange={(e) => setDonationsDraft({...donationsDraft, link: e.target.value})} />
                                <label style={{color: '#8F9EB5', fontSize: 12}}>Pitch Text</label>
                                <RenderEditor value={donationsDraft.text} onChange={(val) => setDonationsDraft({...donationsDraft, text: val})} />
                                <div style={{marginTop: 20}}>
                                    <label style={{color: '#8F9EB5', fontSize: 12}}>Thank You Wall (Bottom Card)</label>
                                    <RenderEditor 
                                    value={donationsDraft.thankYouText || ""} 
                                    onChange={(val) => setDonationsDraft({...donationsDraft, thankYouText: val})} 
                                    />
                                </div>
                            </div>
                        )}
                         {/* === CUSTOM PAGES TAB === */}
                        {activeTab === 'pages' && (
                            <div>
                                <div style={{display:'flex', justifyContent:'space-between', marginBottom: 15}}>
                                    <h3>{pageEditingId ? "Edit Page" : "Create Page"}</h3>
                                    {pageEditingId && <button onClick={()=>{setPageEditingId(null); setPageDraft({title:"", slug:""}); setPageSections([])}} style={{background:'transparent', border:'1px solid #555', color:'#ccc', borderRadius: 4, padding:'5px 10px', cursor: 'pointer'}}>Cancel</button>}
                                </div>
                                
                                {/* TITLE & SLUG INPUTS */}
                                <div style={{display:'flex', gap: 10, marginBottom: 20}}>
                                    <div style={{flex:1}}>
                                        <label style={{color:'#888', fontSize:12}}>Title</label>
                                        {/* Using pageDraft.title */}
                                        <input className="DarkInput" value={pageDraft.title} onChange={e=>setPageDraft(prev => ({...prev, title: e.target.value}))} />
                                    </div>
                                    <div style={{flex:1}}>
                                        <label style={{color:'#888', fontSize:12}}>Slug</label>
                                        {/* Using pageDraft.slug */}
                                        <input 
                                            className="DarkInput" 
                                            value={pageDraft.slug} 
                                            disabled={!!pageEditingId} // Lock slug when editing to prevent broken links
                                            style={{opacity: pageEditingId ? 0.5 : 1, cursor: pageEditingId ? 'not-allowed' : 'text'}}
                                            onChange={e=>setPageDraft(prev => ({...prev, slug: e.target.value}))} 
                                        />
                                    </div>
                                </div>

                                {/* PAGE SECTION BUILDER */}
                                <div style={{marginBottom: 20, background: 'rgba(0,0,0,0.2)', padding: 15, borderRadius: 8}}>
                                    <h4 style={{marginTop:0}}>Content Sections</h4>
                                    <div style={{display:'flex', gap: 10, marginBottom: 15}}>
                                        <button onClick={()=>addPageSection('card')} style={{background: '#6C5DD3', border:'none', color:'white', padding:'8px 12px', borderRadius:6, cursor:'pointer'}}>+ Add Text Card</button>
                                        <button onClick={()=>addPageSection('media')} style={{background: '#26A65B', border:'none', color:'white', padding:'8px 12px', borderRadius:6, cursor:'pointer'}}>+ Add Video/Image</button>
                                    </div>

                                    {/* SECTION LOOP */}
                                    {pageSections.map((section, index) => (
                                        <div key={section.id} style={{background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: 15, marginBottom: 15, border: '1px solid rgba(255,255,255,0.1)'}}>
                                            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 10}}>
                                                <div style={{fontWeight: 'bold', color: section.type==='card' ? '#6C5DD3' : '#26A65B'}}>
                                                    {section.type === 'card' ? "📄 Glass Card" : "🎬 Standalone Media"}
                                                </div>
                                                <div style={{display:'flex', gap: 5}}>
                                                    <button onClick={()=>movePageSection(index, -1)} disabled={index===0} style={{cursor:'pointer', background:'transparent', border:'none', color:'white'}}>▲</button>
                                                    <button onClick={()=>movePageSection(index, 1)} disabled={index===pageSections.length-1} style={{cursor:'pointer', background:'transparent', border:'none', color:'white'}}>▼</button>
                                                    <input type="checkbox" checked={section.visible} onChange={(e)=>updatePageSection(index, 'visible', e.target.checked)} style={{cursor:'pointer', width:20, height:20}} title="Visible?" />
                                                    <button onClick={()=>removePageSection(index)} style={{color:'#FF5C5C', background:'transparent', border:'none', cursor:'pointer', marginLeft: 10}}>Delete</button>
                                                </div>
                                            </div>
                                            
                                            {section.type === 'card' && <RenderEditor value={section.content} onChange={(val) => updatePageSection(index, 'content', val)} />}
                                            {section.type === 'media' && (
                                                <div>
                                                    <label style={{color: '#8F9EB5', fontSize: 12}}>Image URL or YouTube/Twitch Link</label>
                                                    <input className="DarkInput" value={section.content} onChange={(e) => updatePageSection(index, 'content', e.target.value)} placeholder="https://..." />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div style={{textAlign:'right', marginBottom:20}}>
                                    <button className="PrimaryButton" onClick={handleSavePage}>{pageEditingId?"Update Page":"Create Page"}</button>
                                </div>
                                
                                <hr style={{borderColor: 'rgba(255,255,255,0.1)', margin: '20px 0'}} />
                                
                                <h4>Existing Pages</h4>
                                {customPages.map(p => (
                                    <div key={p.id} style={{display:'flex', justifyContent:'space-between', padding: '10px', borderBottom:'1px solid rgba(255,255,255,0.05)'}}>
                                        <span>{p.title} <small style={{color:'#6C5DD3'}}>(/{p.slug})</small></span>
                                        <div>
                                            <button onClick={()=>handleEditPage(p)} style={{color:'#6C5DD3', background:'none', border:'none', cursor:'pointer', marginRight:10}}>Edit</button>
                                            <button onClick={()=>handleDeletePage(p.id)} style={{color:'#FF5C5C', background:'none', border:'none', cursor:'pointer'}}>Delete</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        {/* === USERS TAB === */}
                        {activeTab === 'users' && (

                            <div style={{display: 'flex', flexDirection: 'column', gap: 20}}>
                                
                                <div className="GlassCard" style={{ padding: 20 }}>
                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
                                        <h3 style={{ color: 'white', margin: 0 }}>BBO Username Aliases</h3>
                                        <button className="PrimaryButton" onClick={() => saveWrapper(saveAliasesCsv, aliasesCsv, "Aliases Saved")}>Save Aliases</button>
                                    </div>
                                    <p style={{fontSize: 12, color: '#8F9EB5', marginTop: 0}}>
                                        Map BBO usernames to preferred real names. One per line. Format: <code>bboname, Real Name</code>
                                    </p>
                                    <textarea 
                                        className="DarkInput" 
                                        style={{ height: 100, fontFamily: 'monospace' }} 
                                        value={aliasesCsv} 
                                        onChange={(e) => saveAliasesCsv(e.target.value)} // Local state managed via Context
                                        placeholder="Light19, Isaac Stone&#10;Marksmanma, BridginWithKai" 
                                    />
                                </div>

                            <div className="GlassCard" style={{ padding: 20 }}>
                                <h2 style={{ color: 'white', marginTop: 0 }}>User Roles & Team Assignment</h2>
                                <table style={{ width: '100%', color: 'white', textAlign: 'left', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                                            <th style={{ padding: 10 }}>Email</th>
                                            <th style={{ padding: 10 }}>Role</th>
                                            <th style={{ padding: 10 }}>Team (For Captains)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allUsers.map(u => (
                                            <tr key={u.uid} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                                <td style={{ padding: 10 }}>{u.email}</td>
                                                <td style={{ padding: 10 }}>
                                                    <select 
                                                        className="DarkInput" 
                                                        style={{ margin: 0, padding: 5 }}
                                                        value={u.role || 'user'}
                                                        onChange={(e) => updateUserRoleData(u.uid, { role: e.target.value })}
                                                    >
                                                        <option value="commentator">Commentator</option>
                                                        <option value="captain">Captain</option>
                                                        <option value="admin">Admin</option>
                                                    </select>
                                                </td>
                                                <td style={{ padding: 10 }}>
                                                    <select 
                                                        className="DarkInput" 
                                                        style={{ margin: 0, padding: 5 }}
                                                        // Look for teamName, with a fallback just in case you already saved an ID during testing
                                                        value={u.teamName || ''} 
                                                        onChange={(e) => updateUserRoleData(u.uid, { teamName: e.target.value })}
                                                    >
                                                        <option value="">-- No Team --</option>
                                                        {teams.map(t => (
                                                            <option key={t.name} value={t.name}> {t.name} </option> 
                                                        ))}
                                                    </select>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            </div>

                        )}
                        
                        {/* === SCHEDULE TAB === */}
                        {activeTab === 'schedule' && (
                            <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 20, alignItems: 'start', height: '100%' }}>
                                
                                {/* LEFT: Scrolling Match List */}
                                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', maxHeight: '75vh' }}>
                                    <div style={{ padding: '15px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)', fontWeight: 'bold', fontSize: 14 }}>
                                        Matches ({schedule.length})
                                    </div>
                                    <div className="CustomScroll" style={{ overflowY: 'auto', flex: 1 }}>
                                        {schedule.length === 0 ? (
                                            <div style={{ padding: 20, textAlign: 'center', color: '#888', fontSize: 12 }}>No matches yet.</div>
                                        ) : (
                                            schedule.map(item => {
                                                // Create a tiny preview string
                                                const t1 = teams.find(t => t.id.toString() === item.team1?.toString())?.name || "TBA";
                                                const t2 = teams.find(t => t.id.toString() === item.team2?.toString())?.name || "TBA";
                                                const timeStr = item.startTime ? DateTime.fromISO(item.startTime).toFormat('MMM d, h:mm a') : "No Time";

                                                return (
                                                    <div 
                                                        key={item.id} 
                                                        onClick={() => startEditSched(item)} 
                                                        style={{
                                                            padding: '12px 15px', borderBottom: '1px solid rgba(255,255,255,0.03)', cursor: 'pointer', transition: 'all 0.2s',
                                                            borderLeft: schedEditingId === item.id ? '4px solid #6C5DD3' : '4px solid transparent', 
                                                            background: schedEditingId === item.id ? 'rgba(108,93,211,0.1)' : 'transparent'
                                                        }}
                                                    >
                                                        <div style={{ fontSize: 13, fontWeight: 'bold', color: schedEditingId === item.id ? '#fff' : '#ccc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {t1} vs {t2}
                                                        </div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                                                            <small style={{ color: '#888', fontSize: 11 }}>{timeStr}</small>
                                                            <button onClick={(e)=>{e.stopPropagation();deleteSched(item.id)}} style={{ color: '#FF5C5C', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 10 }}>Del</button>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>

                                {/* RIGHT: The Editor */}
                                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 12, display: 'flex', flexDirection: 'column', maxHeight: '75vh' }}>
                                    
                                    {/* FIXED HEADER */}
                                    <div style={{ padding: '20px 25px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                                        <h3 style={{ margin: 0 }}>{schedEditingId ? "Edit Match" : "Create Match"}</h3>
                                        <div style={{ display: 'flex', gap: 10 }}>
                                            {schedEditingId && <button onClick={cancelEditSched} style={{ background: 'transparent', border: '1px solid #555', color: '#aaa', borderRadius: 4, padding: '5px 10px', cursor: 'pointer' }}>Cancel Edit</button>}
                                            <button className="PrimaryButton" style={{ margin: 0 }} onClick={handleSchedSubmit}>{schedEditingId ? "Update Match" : "Create Match"}</button>
                                        </div>
                                    </div>
                                    
                                    {/* SCROLLING BODY */}
                                    <div className="CustomScroll" style={{ padding: 25, overflowY: 'auto', flex: 1 }}>
                                        
                                        <div style={{display:'grid', gridTemplateColumns: '1fr 1fr', gap: 15, marginBottom: 15}}>
                                            <div><label style={{fontSize:11, color:'#888'}}>Team 1</label><select className="DarkInput" value={schedForm.team1} onChange={e=>setSchedForm({...schedForm, team1: e.target.value})}><option value="">Select Team</option>{teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div>
                                            <div><label style={{fontSize:11, color:'#888'}}>Team 2</label><select className="DarkInput" value={schedForm.team2} onChange={e=>setSchedForm({...schedForm, team2: e.target.value})}><option value="">Select Team</option>{teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div>
                                            <div><label style={{fontSize:11, color:'#888'}}>Start Time</label><input type="datetime-local" className="DarkInput" value={schedForm.startTime} onChange={e=>setSchedForm({...schedForm, startTime: e.target.value})}/></div>
                                            <div><label style={{fontSize:11, color:'#888'}}>End Time</label><input type="datetime-local" className="DarkInput" value={schedForm.endTime} onChange={e=>setSchedForm({...schedForm, endTime: e.target.value})}/></div>
                                        </div>
                                    
                                    
                                        {/* MATCH METADATA */}
                                        <div style={{display:'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 15, marginBottom: 15, background: 'rgba(255,255,255,0.02)', padding: 10, borderRadius: 8}}>
                                            <div><label style={{fontSize:11, color:'#888'}}>Stage</label><select className="DarkInput" style={{margin:0}} value={schedForm.stage} onChange={e=>setSchedForm({...schedForm, stage: e.target.value})}><option value="RR">Round Robin</option><option value="KO">Knockout</option><option value="Final">Final</option></select></div>
                                            <div><label style={{fontSize:11, color:'#888'}}>Group / Phase Name</label><input className="DarkInput" style={{margin:0}} value={schedForm.group} onChange={e=>setSchedForm({...schedForm, group: e.target.value})} placeholder="e.g. Group A" /></div>
                                            <div><label style={{fontSize:11, color:'#888'}}>Board Count (Base)</label><input type="number" className="DarkInput" style={{margin:0}} value={schedForm.boardCount} onChange={e=>setSchedForm({...schedForm, boardCount: parseInt(e.target.value) || 8})} /></div>
                                        </div>

                                        {/* SCORING ENGINE */}
                                        <div style={{border: '1px solid rgba(108,93,211,0.3)', padding: 15, borderRadius: 8, marginBottom: 15}}>
                                            <h4 style={{margin: '0 0 10px 0', color: '#A395E9', fontSize: 13}}>Bridge Scoring Engine</h4>
                                            
                                            <div style={{marginBottom: 15}}>
                                                <label style={{fontSize:11, color:'#888'}}>Paste Raw .LIN Data (Auto-calculates IMPs & Assigns Players)</label>
                                                <textarea className="DarkInput" style={{margin:0, height: 60}} value={schedForm.linData} onChange={e=>setSchedForm({...schedForm, linData: e.target.value})} placeholder="vg|Final...|pn|..." />
                                                <button onClick={handleProcessLin} style={{background: '#6C5DD3', border: 'none', color: '#fff', borderRadius: 4, padding: '6px 12px', fontSize: 11, marginTop: 5, cursor: 'pointer', fontWeight: 'bold'}}>Process LIN File</button>
                                            </div>

                                            {schedForm.parsedPlayers && schedForm.parsedPlayers.length >= 8 && (
                                                <div style={{background: 'rgba(0,0,0,0.4)', borderRadius: 8, padding: 15, marginBottom: 15, border: '1px dashed #6C5DD3'}}>
                                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
                                                        <h5 style={{margin: 0, color: '#fff', fontSize: 13}}>BBO Seating Data</h5>
                                                        <button onClick={handleSwapLinTeams} style={{background: 'transparent', border: '1px solid #FFD700', color: '#FFD700', borderRadius: 4, padding: '4px 8px', fontSize: 10, cursor: 'pointer', fontWeight: 'bold', transition: '0.2s'}}>
                                                            🔄 Swap Team 1 & Team 2
                                                        </button>
                                                    </div>
                                                    <div style={{display: 'flex', gap: 15, fontSize: 11, color: '#ccc'}}>
                                                        <div style={{flex: 1, background: 'rgba(255,255,255,0.05)', padding: 10, borderRadius: 6}}>
                                                            <strong style={{color: '#A395E9'}}>LIN Team 1: {schedForm.linTeam1}</strong><br/>
                                                            <div style={{marginTop: 5}}><strong>Open N/S:</strong> {schedForm.parsedPlayers[2]} & {schedForm.parsedPlayers[0]}</div>
                                                            <div><strong>Closed E/W:</strong> {schedForm.parsedPlayers[7]} & {schedForm.parsedPlayers[5]}</div>
                                                        </div>
                                                        <div style={{flex: 1, background: 'rgba(255,255,255,0.05)', padding: 10, borderRadius: 6}}>
                                                            <strong style={{color: '#A395E9'}}>LIN Team 2: {schedForm.linTeam2}</strong><br/>
                                                            <div style={{marginTop: 5}}><strong>Open E/W:</strong> {schedForm.parsedPlayers[3]} & {schedForm.parsedPlayers[1]}</div>
                                                            <div><strong>Closed N/S:</strong> {schedForm.parsedPlayers[6]} & {schedForm.parsedPlayers[4]}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {schedForm.parsedBoards && schedForm.parsedBoards.length > 0 && (
                                                <div style={{background: 'rgba(0,0,0,0.4)', borderRadius: 8, padding: 10, marginBottom: 15}}>
                                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
                                                        <h5 style={{margin: 0, color: '#fff'}}>Board Segment Assignments</h5>
                                                        <button 
                                                            onClick={(e) => { e.preventDefault(); handleSwapAllBoards(); }}
                                                            style={{
                                                                background: schedForm.isAllSwapped ? '#FFD700' : 'transparent', 
                                                                border: '1px solid #FFD700', 
                                                                color: schedForm.isAllSwapped ? '#000' : '#FFD700', 
                                                                borderRadius: 4, padding: '4px 12px', fontSize: 10, cursor: 'pointer', fontWeight: 'bold', transition: '0.2s',
                                                                boxShadow: schedForm.isAllSwapped ? '0 0 10px rgba(255,215,0,0.4)' : 'none'
                                                            }}
                                                        >
                                                            {schedForm.isAllSwapped ? '✅ ALL ROOMS SWAPPED' : '🔄 Swap All Rooms'}
                                                        </button>
                                                    </div>
                                                    <div style={{maxHeight: 200, overflowY: 'auto', paddingRight: 5}} className="CustomScroll">
                                                        <table style={{width: '100%', fontSize: 11, color: '#ccc', textAlign: 'left', borderCollapse: 'collapse'}}>
                                                            <thead>
                                                                <tr style={{borderBottom: '1px solid #444'}}>
                                                                    <th style={{paddingBottom: 5}}>Bd</th>
                                                                    <th style={{width: 30}}></th>
                                                                    <th>Rm 1</th>
                                                                    <th>Rm 2</th>
                                                                    <th>IMPs</th>
                                                                    <th>Segment</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {schedForm.parsedBoards.map((b, idx) => (
                                                                    <tr key={idx} style={{borderBottom: '1px solid rgba(255,255,255,0.05)', opacity: b.segment === 'Omitted' ? 0.4 : 1}}>
                                                                        <td style={{padding: '4px 0'}}>{b.boardNum}</td>
                                                                        {/* <td>
                                                                            <button 
                                                                                onClick={(e) => { e.preventDefault(); handleSwapSingleBoard(idx); }}
                                                                                title="Swap Room 1 and Room 2 for this board"
                                                                                style={{background: 'transparent', border: '1px solid #555', color: '#888', borderRadius: 4, cursor: 'pointer', padding: '2px 4px', fontSize: 10, transition: '0.2s'}}
                                                                            >
                                                                                🔄
                                                                            </button>
                                                                        </td> */}
                                                                        <td>{b.room1 || '-'}</td>
                                                                        <td>{b.room2 || '-'}</td>
                                                                        <td style={{color: b.imps1 > 0 ? '#26A65B' : b.imps2 > 0 ? '#FF5C5C' : '#888'}}>
                                                                            {b.imps1 > 0 ? `+${b.imps1}` : b.imps2 > 0 ? `-${b.imps2}` : '0'}
                                                                        </td>
                                                                        <td>
                                                                            <select 
                                                                                value={b.segment} 
                                                                                onChange={(e) => handleBoardSegmentChange(idx, e.target.value)}
                                                                                style={{background: '#222', color: '#fff', border: '1px solid #444', borderRadius: 4, padding: 2}}
                                                                            >
                                                                                <option value="Main">Main Match</option>
                                                                                <option value="TB1">Tiebreak 1</option>
                                                                                <option value="TB2">Tiebreak 2</option>
                                                                                <option value="Omitted">Omitted</option>
                                                                            </select>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}

                                            <div style={{display:'flex', gap: 15, alignItems: 'center'}}>
                                                <div style={{flex: 1}}><label style={{fontSize:11, color:'#888'}}>Team 1 IMPs</label><input type="number" className="DarkInput" style={{margin:0}} value={schedForm.team1_score} onChange={e=>setSchedForm({...schedForm, team1_score: e.target.value})} /></div>
                                                <div style={{flex: 1}}><label style={{fontSize:11, color:'#888'}}>Team 2 IMPs</label><input type="number" className="DarkInput" style={{margin:0}} value={schedForm.team2_score} onChange={e=>setSchedForm({...schedForm, team2_score: e.target.value})} /></div>
                                                <div style={{flex: 1, paddingTop: 15}}>
                                                    <button onClick={handleAutoCalcVP} style={{background: '#26A65B', color: 'white', border: 'none', borderRadius: 4, width: '100%', padding: '8px', cursor: 'pointer', fontWeight: 'bold'}}>Calculate VPs</button>
                                                </div>
                                            </div>

                                            <div style={{display:'flex', gap: 15, marginTop: 10, background: 'rgba(0,0,0,0.3)', padding: 10, borderRadius: 6}}>
                                                <div style={{flex: 1}}><label style={{fontSize:11, color:'#FFD700'}}>Team 1 VP</label><input type="number" step="0.01" className="DarkInput" style={{margin:0}} value={schedForm.team1_vp} onChange={e=>setSchedForm({...schedForm, team1_vp: e.target.value})} /></div>
                                                <div style={{flex: 1}}><label style={{fontSize:11, color:'#FFD700'}}>Team 2 VP</label><input type="number" step="0.01" className="DarkInput" style={{margin:0}} value={schedForm.team2_vp} onChange={e=>setSchedForm({...schedForm, team2_vp: e.target.value})} /></div>
                                                <div style={{flex: 1}}><label style={{fontSize:11, color:'#FF5C5C'}}>T1 Penalty (-VP)</label><input type="number" step="0.01" className="DarkInput" style={{margin:0}} value={schedForm.team1_penalty} onChange={e=>setSchedForm({...schedForm, team1_penalty: e.target.value})} /></div>
                                                <div style={{flex: 1}}><label style={{fontSize:11, color:'#FF5C5C'}}>T2 Penalty (-VP)</label><input type="number" step="0.01" className="DarkInput" style={{margin:0}} value={schedForm.team2_penalty} onChange={e=>setSchedForm({...schedForm, team2_penalty: e.target.value})} /></div>
                                            </div>

                                            {/* KO TIEBREAKERS: BEER & BAM */}
                                        <div style={{display:'flex', gap: 15, marginTop: 10, background: 'rgba(0,0,0,0.3)', padding: 10, borderRadius: 6}}>
                                            <div style={{flex: 1}}><label style={{fontSize:11, color:'#F39C12', fontWeight: 'bold'}}>T1 Beer Pts</label><input type="number" step="1" className="DarkInput" style={{margin:0}} value={schedForm.team1_beer} onChange={e=>setSchedForm({...schedForm, team1_beer: e.target.value})} /></div>
                                            <div style={{flex: 1}}><label style={{fontSize:11, color:'#F39C12', fontWeight: 'bold'}}>T2 Beer Pts</label><input type="number" step="1" className="DarkInput" style={{margin:0}} value={schedForm.team2_beer} onChange={e=>setSchedForm({...schedForm, team2_beer: e.target.value})} /></div>
                                            <div style={{flex: 1}}><label style={{fontSize:11, color:'#8F9EB5'}}>T1 BAM</label><input type="number" step="0.5" className="DarkInput" style={{margin:0}} value={schedForm.team1_bam} onChange={e=>setSchedForm({...schedForm, team1_bam: e.target.value})} /></div>
                                            <div style={{flex: 1}}><label style={{fontSize:11, color:'#8F9EB5'}}>T2 BAM</label><input type="number" step="0.5" className="DarkInput" style={{margin:0}} value={schedForm.team2_bam} onChange={e=>setSchedForm({...schedForm, team2_bam: e.target.value})} /></div>
                                        </div>

                                        {/* MANUAL WINNER OVERRIDE (For Chat Votes / Manual Advancement) */}
                                        <div style={{display:'flex', gap: 15, marginTop: 10, background: 'rgba(0,0,0,0.3)', padding: 10, borderRadius: 6, alignItems: 'center'}}>
                                            <div style={{flex: 1}}>
                                                <label style={{fontSize:11, color:'#FFD700', fontWeight: 'bold'}}>Knockout Winner Override</label>
                                                <select className="DarkInput" style={{margin:0}} value={schedForm.winner || ""} onChange={e=>setSchedForm({...schedForm, winner: e.target.value})}>
                                                    <option value="">Auto-Calculate (IMPs ➔ Beer ➔ BAM)</option>
                                                    <option value="team1">Force Team 1 to Advance</option>
                                                    <option value="team2">Force Team 2 to Advance</option>
                                                </select>
                                            </div>
                                            <div style={{flex: 1, fontSize: 10, color: '#888', paddingTop: 14, lineHeight: 1.3}}>
                                                * Use this to force a team forward (e.g., after a chat vote tiebreaker). Overrides all scores.
                                            </div>
                                        </div>

                                            <div style={{marginTop: 15, display: 'flex', alignItems: 'center', gap: 10}}>
                                                <input type="checkbox" id="publishResult" checked={schedForm.isFinal} onChange={e=>setSchedForm({...schedForm, isFinal: e.target.checked})} style={{cursor: 'pointer', width: 16, height: 16}} />
                                                <label htmlFor="publishResult" style={{fontSize: 13, color: '#fff', fontWeight: 'bold', cursor: 'pointer'}}>Publish Result to Public Schedule & Standings</label>
                                            </div>
                                        </div>

                                        <input className="DarkInput" placeholder="Custom Display String (Optional, e.g. 'T1 wins in OT')" value={schedForm.result} onChange={e=>setSchedForm({...schedForm, result: e.target.value})} />
                                        
                                        <div style={{margin: '15px 0'}}>
                                            <label style={{fontSize:12, color:'#888', marginBottom: 5, display: 'block'}}>Stream Perspectives</label>
                                            {schedForm.links.map((linkObj, index) => (
                                                <div key={index} style={{display:'flex', gap: 10, marginBottom: 5, alignItems: 'center'}}>
                                                    <div style={{display: 'flex', flexDirection: 'column'}}>
                                                        <button onClick={(e)=>{e.preventDefault(); moveLinkUp(index)}} disabled={index===0} style={{cursor: index===0 ? 'default' : 'pointer', background:'transparent', border:'none', color: index===0 ? '#444' : 'white', padding: '0 4px', fontSize: 10}}>▲</button>
                                                        <button onClick={(e)=>{e.preventDefault(); moveLinkDown(index)}} disabled={index===schedForm.links.length-1} style={{cursor: index===schedForm.links.length-1 ? 'default' : 'pointer', background:'transparent', border:'none', color: index===schedForm.links.length-1 ? '#444' : 'white', padding: '0 4px', fontSize: 10}}>▼</button>
                                                    </div>
                                                    
                                                    <input className="DarkInput" style={{margin:0, flex: 2}} placeholder="URL" value={linkObj.url} onChange={(e)=>handleLinkChange(index, 'url', e.target.value)}/>
                                                    <input className="DarkInput" style={{margin:0, flex: 1}} placeholder="Label" value={linkObj.label} onChange={(e)=>handleLinkChange(index, 'label', e.target.value)}/>
                                                    <select className="DarkInput" style={{margin:0, flex: 1}} value={linkObj.teamId} onChange={(e)=>handleLinkChange(index, 'teamId', e.target.value)}>
                                                        <option value="">Team?</option>
                                                        {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                                    </select>
                                                    <select className="DarkInput" style={{margin:0, flex: 1}} value={linkObj.associatedTeamId || ''} onChange={(e)=>handleLinkChange(index, 'associatedTeamId', e.target.value)}>
                                                        <option value="">Assoc?</option>
                                                        {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                                    </select>
                                                    <button onClick={(e)=>{e.preventDefault(); handleRemoveLink(index)}} style={{background:'#FF5C5C', color:'white', border:'none', borderRadius: 8, width: 30, height: 30, cursor:'pointer'}}>✕</button>
                                                </div>
                                            ))}
                                            <button onClick={(e)=>{e.preventDefault(); handleAddLink()}} style={{background:'transparent', border:'1px dashed #6C5DD3', color:'#6C5DD3', borderRadius: 8, width: '100%', padding: '8px', cursor:'pointer', fontSize: 13}}>+ Add Perspective</button>
                                        </div>
                                        <textarea className="DarkInput" placeholder="Notes" rows={2} value={schedForm.description} onChange={e=>setSchedForm({...schedForm, description: e.target.value})}/>
                                        
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* === TEAMS TAB === */}
                        {activeTab === 'teams' && (
                            <div>
                                {/* --- INPUT FORM --- */}
                                <div style={{display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end', marginBottom:20, background:'rgba(0,0,0,0.2)', padding:15, borderRadius:10}}>
                                    
                                    {/* Name Input */}
                                    <div style={{flex:1, minWidth: 150}}>
                                        <label style={{fontSize:11, color:'#888'}}>Name</label>
                                        <input className="DarkInput" style={{margin:0}} value={teamDraft.name} onChange={e=>setTeamDraft({...teamDraft, name:e.target.value})}/>
                                    </div>
                                    
                                    {/* Color Picker */}
                                    <div>
                                        <label style={{fontSize:11, color:'#888', display:'block'}}>Color</label>
                                        <input type="color" value={teamDraft.color} onChange={e=>setTeamDraft({...teamDraft, color:e.target.value})} style={{height:42, width:50, border:'none', background:'transparent', cursor:'pointer'}}/>
                                    </div>
                                    
                                    {/* OPTION A: SVG Upload */}
                                    <div style={{flex:1, minWidth: 150}}>
                                        <label style={{fontSize:11, color:'#888'}}>Option A: Upload SVG</label>
                                        <input 
                                            id="logo-upload" 
                                            type="file" 
                                            accept=".svg" 
                                            onChange={(e) => {
                                                handleLogoFileChange(e);
                                                // Clear the image URL if they upload an SVG
                                                setTeamDraft(prev => ({...prev, logoUrl: ""}));
                                            }} 
                                            style={{fontSize:12, color: '#ccc'}}
                                        />
                                    </div>

                                    {/* Separator */}
                                    <div style={{fontSize: 10, fontWeight: 'bold', color: '#666', paddingBottom: 12}}>OR</div>

                                    {/* OPTION B: Select Media Image */}
                                    <div style={{flex:1, minWidth: 150}}>
                                        <label style={{fontSize:11, color:'#888'}}>Option B: Select Media</label>
                                        <select 
                                            className="DarkInput" 
                                            style={{margin:0}} 
                                            value={teamDraft.logoUrl || ""} 
                                            onChange={(e) => {
                                                setTeamDraft({
                                                    ...teamDraft, 
                                                    logoUrl: e.target.value, 
                                                    logo: "" // Clear SVG if they select an image
                                                });
                                                // Reset the file input visually
                                                document.getElementById('logo-upload').value = "";
                                            }}
                                        >
                                            <option value="">-- No Image --</option>
                                            {AVAILABLE_IMAGES.map((img) => (
                                                <option key={img.filename} value={img.filename}>{img.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div style={{width: '100%', marginTop: 10}}>
                                        <label style={{fontSize:11, color:'#888'}}>Creator Bio (Shows in Home Carousel)</label>
                                        <textarea 
                                            className="DarkInput" 
                                            style={{margin:0, height: 80, resize: 'vertical'}} 
                                            value={teamDraft.bio || ""} 
                                            onChange={e => setTeamDraft({...teamDraft, bio: e.target.value})}
                                            placeholder="Tell the community about this creator..."
                                        />
                                    </div>

                                    <div style={{width: '100%', display: 'flex', gap: 15, marginTop: 10}}>
                                        <div style={{flex: 2}}>
                                            <label style={{fontSize:11, color:'#888'}}>Primary Social Link (Twitch/YouTube/X)</label>
                                            <input className="DarkInput" style={{margin:0}} value={teamDraft.socialLink || ""} onChange={e=>setTeamDraft({...teamDraft, socialLink: e.target.value})} placeholder="https://..."/>
                                        </div>
                                        <div style={{flex: 1}}>
                                            <label style={{fontSize:11, color:'#888'}}>Round Robin Group</label>
                                            <input className="DarkInput" style={{margin:0}} value={teamDraft.rrGroup || ""} onChange={e=>setTeamDraft({...teamDraft, rrGroup: e.target.value})} placeholder="e.g. Group A"/>
                                        </div>
                                    </div>

                                    {/* Preview & Submit */}
                                    <div style={{marginBottom: 5}}>{renderLogoPreview(teamDraft)}</div>
                                    
                                    <button className="PrimaryButton" onClick={handleTeamSubmit}>{teamEditingId?"Update":"Add"}</button>
                                    {teamEditingId && <button onClick={cancelEditTeam} style={{color:'#ccc', background:'none', border:'none', cursor:'pointer'}}>Cancel</button>}
                                </div>

                                {/* --- TEAMS LIST --- */}
                                {teams.map(t => (
                                    <div key={t.id} style={{display:'flex', alignItems:'center', padding:'10px', borderBottom:'1px solid rgba(255,255,255,0.05)'}}>
                                        {/* Use new helper to render list item */}
                                        {renderLogoPreview(t)}
                                        
                                        <span style={{marginLeft:15, flex:1, fontWeight:'bold'}}>{t.name}</span>
                                        <button onClick={()=>startEditTeam(t)} style={{color:'#6C5DD3', background:'none', border:'none', cursor:'pointer', marginRight:10}}>Edit</button>
                                        <button onClick={()=>handleDeleteTeam(t.id)} style={{color:'#FF5C5C', background:'none', border:'none', cursor:'pointer'}}>Del</button>
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        {/* === NAVIGATION TAB === */}
                        {activeTab === 'nav' && (
                            <div>
                                <h3>Navigation Menu</h3>
                                <table style={{width: '100%', borderCollapse: 'collapse', marginTop: 20}}>
                                    <thead><tr style={{textAlign:'left', color:'#888'}}><th style={{width:50}}>Sort</th><th>Link</th><th>Visible?</th><th>Label</th></tr></thead>
                                    <tbody>
                                        {navDraft.map((item, idx) => (
                                            <tr key={idx} style={{borderBottom: '1px solid rgba(255,255,255,0.05)'}}>
                                                <td>
                                                    <button onClick={()=>moveNav(idx, -1)} disabled={idx===0} style={{cursor:'pointer', background:'transparent', border:'none', color: idx===0?'#444':'white'}}>▲</button>
                                                    <button onClick={()=>moveNav(idx, 1)} disabled={idx===navDraft.length-1} style={{cursor:'pointer', background:'transparent', border:'none', color: idx===navDraft.length-1?'#444':'white'}}>▼</button>
                                                </td>
                                                <td style={{padding: 10, color: '#8F9EB5', fontSize:13}}>{item.link}</td>
                                                <td style={{padding: 10}}><input type="checkbox" checked={item.visible !== false} onChange={()=>{const n=[...navDraft]; n[idx].visible=!n[idx].visible; setNavDraft(n);}} style={{width: 18, height: 18, cursor: 'pointer'}} /></td>
                                                <td style={{padding: 10}}><input className="DarkInput" style={{margin: 0, padding: 8}} value={item.title} onChange={(e) => {const n=[...navDraft]; n[idx].title=e.target.value; setNavDraft(n);}} /></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div style={{textAlign:'right', marginTop: 20}}><button className="PrimaryButton" onClick={()=>saveWrapper(saveNavigation, navDraft, "Navigation Saved")}>Save Navigation</button></div>
                            </div>
                        )}

                        {/* === STYLE TAB === */}

                        {activeTab === 'style' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 25 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h3 style={{margin: 0}}>Global Styles</h3>
                                    
                                    {/* PREVIEW TOGGLE */}
                                    <button 
                                        onClick={() => setPreviewMode(!previewMode)}
                                        style={{
                                            background: previewMode ? 'rgba(74, 222, 128, 0.2)' : 'transparent',
                                            color: previewMode ? '#4ADE80' : '#8F9EB5',
                                            border: `1px solid ${previewMode ? '#4ADE80' : '#555'}`,
                                            padding: '8px 16px', borderRadius: 8, fontWeight: 'bold', cursor: 'pointer', transition: '0.2s'
                                        }}
                                    >
                                        {previewMode ? 'Previewing Locally' : 'Preview Locally'}
                                    </button>
                                </div>

                                {/* THEME PRESET SELECTOR */}
                                <div style={{ background: 'rgba(0,0,0,0.2)', padding: 15, borderRadius: 12, border: '1px solid rgba(255,255,255,0.05)' }}>
                                    <label style={{color: '#8F9EB5', display: 'block', marginBottom: 8, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1}}>Load Saved Theme</label>
                                    <div style={{ display: 'flex', gap: 10 }}>
                                        <select 
                                            className="DarkInput" 
                                            value={styleDraft?.name || ""}
                                            style={{ margin: 0, flex: 1 }}
                                            onChange={(e) => {
                                                const selected = availableThemes.find(t => t.name === e.target.value);
                                                if (selected) setStyleDraft(selected);
                                            }}
                                        >
                                            {availableThemes.map(t => (
                                                <option key={`opt_${t.name}`} value={t.name}>{t.name}</option>
                                            ))}
                                        </select>

                                        {/* DELETE BUTTON (Safely hidden for Default and Active themes) */}
                                        {styleDraft?.id && styleDraft.id !== 'default' && styleDraft.id !== 'global_active' && (
                                            <button 
                                                onClick={() => {
                                                    if (window.confirm(`Delete the "${styleDraft.name}" preset?`)) {
                                                        const updatedPresets = (themePresets || []).filter(t => t.name.toLowerCase() !== styleDraft.name.toLowerCase());
                                                        saveWrapper(saveThemePresets, updatedPresets, "Preset Deleted!");
                                                        
                                                        // Reset draft to the global style after deletion
                                                        if (globalStyle) setStyleDraft({ ...globalStyle, id: 'global_active' });
                                                    }
                                                }}
                                                style={{ background: 'rgba(229,62,62,0.15)', color: '#E53E3E', border: '1px solid rgba(229,62,62,0.3)', borderRadius: 8, padding: '0 15px', cursor: 'pointer', fontWeight: 'bold' }}
                                                title="Delete this preset"
                                            >
                                                🗑️ Delete
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* THEME EDITOR (Your original controls) */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                                    <div>
                                        <label style={{color: '#8F9EB5', display: 'block', marginBottom: 5, fontSize: 12}}>Font Family</label>
                                        <select className="DarkInput" value={styleDraft.font} onChange={(e) => setStyleDraft({...styleDraft, font: e.target.value})} style={{margin: 0}}>
                                            <option value="Inter">Inter</option>
                                            <option value="Roboto">Roboto</option>
                                            <option value="Open Sans">Open Sans</option>
                                            <option value="Lato">Lato</option>
                                            <option value="Montserrat">Montserrat</option>
                                            <option value="Playfair Display">Playfair Display</option>
                                            <option value="Courier New">Courier New</option>
                                        </select>
                                    </div>
                                    <div style={{display:'grid', gridTemplateColumns: '1fr 1fr', gap: 20}}>
                                        <div><label style={{color: '#8F9EB5', display: 'block', fontSize: 12}}>Accent Color</label><input type="color" value={styleDraft.accentColor} onChange={(e)=>setStyleDraft({...styleDraft, accentColor: e.target.value})} style={{height: 40, width: '100%', border:'none', background:'transparent', cursor:'pointer'}} /></div>
                                        <div><label style={{color: '#8F9EB5', display: 'block', fontSize: 12}}>Sidebar</label><input type="color" value={styleDraft.sidebarBg} onChange={(e)=>setStyleDraft({...styleDraft, sidebarBg: e.target.value})} style={{height: 40, width: '100%', border:'none', background:'transparent', cursor:'pointer'}} /></div>
                                        <div><label style={{color: '#8F9EB5', display: 'block', fontSize: 12}}>BG Start</label><input type="color" value={styleDraft.bgStart} onChange={(e)=>setStyleDraft({...styleDraft, bgStart: e.target.value})} style={{height: 40, width: '100%', border:'none', background:'transparent', cursor:'pointer'}} /></div>
                                        <div><label style={{color: '#8F9EB5', display: 'block', fontSize: 12}}>BG End</label><input type="color" value={styleDraft.bgEnd} onChange={(e)=>setStyleDraft({...styleDraft, bgEnd: e.target.value})} style={{height: 40, width: '100%', border:'none', background:'transparent', cursor:'pointer'}} /></div>
                                    </div>
                                </div>

                                {/* SAVE ACTIONS */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 15, marginTop: 15, paddingTop: 20, borderTop: '1px dashed rgba(255,255,255,0.1)' }}>
                                    
                                    {/* Create New Preset */}
                                    <div style={{ display: 'flex', gap: 10 }}>
                                        <input 
                                            className="DarkInput" 
                                            placeholder="Preset Name..." 
                                            value={newThemeName} 
                                            onChange={e => setNewThemeName(e.target.value)} 
                                            style={{ margin: 0, flex: 1 }} 
                                        />
                                        <button 
                                            onClick={() => {
                                                const trimmedName = newThemeName.trim();
                                                if(!trimmedName) return alert("Please enter a name for the preset.");
                                                
                                                const nameExists = (themePresets || []).some(t => t.name.toLowerCase() === trimmedName.toLowerCase());
                                                if (nameExists) return alert("A preset with this name already exists. Please choose a different name.");

                                                const { id, name, ...cleanDraft } = styleDraft;
                                                const newTheme = { ...cleanDraft, id: `theme_${Date.now()}`, name: trimmedName };
                                                
                                                // CRITICAL: Filter the ghosts out of the existing array before saving, cleaning the DB!
                                                const scrubbedPresets = (themePresets || []).filter(t => 
                                                    t.id !== 'default' && t.id !== 'current' && t.name !== 'Current Active (Unsaved)'
                                                );
                                                
                                                const updatedPresets = [...scrubbedPresets, newTheme];
                                                
                                                saveWrapper(saveThemePresets, updatedPresets, "Preset Saved!");
                                                setNewThemeName("");
                                            }}
                                            style={{ background: 'transparent', border: '1px solid #6C5DD3', color: '#A395E9', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold' }}
                                        >
                                            Save as New Preset
                                        </button>
                                    </div>

                                    {/* Apply Global */}
                                    <div style={{ display: 'flex', gap: 10 }}>
                                        <button 
                                            className="PrimaryButton" 
                                            style={{ flex: 2, padding: '15px' }}
                                            onClick={() => {
                                                let payload = { ...styleDraft };

                                                // 1. Find the unmodified original version of this theme from the dropdown list
                                                const originalTheme = availableThemes.find(t => t.id === payload.id);
                                                
                                                // 2. Check if ANY colors or fonts were actually changed (The "Dirty Check")
                                                const isDirty = !originalTheme || 
                                                                originalTheme.font !== payload.font ||
                                                                originalTheme.accentColor !== payload.accentColor ||
                                                                originalTheme.bgStart !== payload.bgStart ||
                                                                originalTheme.bgEnd !== payload.bgEnd ||
                                                                originalTheme.sidebarBg !== payload.sidebarBg;

                                                // 3. IF NOTHING CHANGED: Just publish it instantly. No modals, no renaming.
                                                if (!isDirty) {
                                                    setPreviewMode(false); 
                                                    return saveWrapper(saveGlobalStyle, payload, "Style Applied Globally");
                                                }

                                                // 4. IF IT IS DIRTY: Identify if this is a protected theme
                                                const isProtected = !payload.id ||
                                                                    payload.id === 'default' || 
                                                                    payload.id === 'current' || 
                                                                    payload.id === 'global_active';

                                                if (isProtected) {
                                                    // Force a "Save As" for tweaked protected themes
                                                    const newName = window.prompt("⚠️ Name Required\n\nYou tweaked a protected theme.\n\nPlease enter a new name to save and publish this theme:");
                                                    
                                                    if (!newName || !newName.trim()) {
                                                        return alert("Publish aborted: You must provide a valid name.");
                                                    }

                                                    const nameExists = (themePresets || []).some(t => t.name.toLowerCase() === newName.trim().toLowerCase());
                                                    if (nameExists) return alert("Publish aborted: A preset with that name already exists.");

                                                    payload.name = newName.trim();
                                                    payload.id = `theme_${Date.now()}`;

                                                    const scrubbedPresets = (themePresets || []).filter(t => t.id !== 'default' && t.id !== 'current' && t.id !== 'global_active');
                                                    saveThemePresets([...scrubbedPresets, payload]); 
                                                    setStyleDraft(payload);

                                                } else {
                                                    // It is a custom saved theme that was dirtied. Ask to overwrite.
                                                    const confirmEdit = window.confirm(`⚠️ Overwrite Preset?\n\nYou made changes to "${payload.name}".\n\nDo you want to overwrite the saved preset in place?`);
                                                    if (!confirmEdit) return; // Abort

                                                    const scrubbedPresets = (themePresets || []).filter(t => t.id !== 'default' && t.id !== 'current' && t.id !== 'global_active' && t.id !== payload.id);
                                                    saveThemePresets([...scrubbedPresets, payload]);
                                                }

                                                // 5. Finally, push the safely updated theme to the live site
                                                setPreviewMode(false); 
                                                saveWrapper(saveGlobalStyle, payload, "Style Applied Globally");
                                            }}
                                        >
                                            🚀 Publish to Live Site
                                        </button>
                                        <button 
                                            style={{ flex: 1, background:'transparent', border:'1px solid rgba(229, 62, 62, 0.4)', color:'#E53E3E', borderRadius:8, cursor:'pointer', fontWeight: 'bold' }} 
                                            onClick={() => {
                                                if(window.confirm("Reset to Default Style?")) {
                                                    // EXPLICITLY pass Roboto, the default ID, and the default Name!
                                                    const defaults = { id: 'default', name: 'Default Purple', font: 'Roboto', accentColor: '#6C5DD3', bgStart: '#7e6cd9', bgEnd: '#4a32e1', sidebarBg: '#110e22', cardBg: 'rgba(20, 20, 35, 0.6)' };
                                                    setStyleDraft(defaults);
                                                    setPreviewMode(false);
                                                    saveWrapper(saveGlobalStyle, defaults, "Default Style Selected");
                                                }
                                            }}
                                        >
                                            Reset to Defaults
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* {activeTab === 'style' && (
                            <div>
                                <h3>Global Styles</h3>
                                <div style={{marginBottom: 20}}>
                                    <label style={{color: '#8F9EB5', display: 'block', marginBottom: 5}}>Font Family</label>
                                    <select className="DarkInput" value={styleDraft.font} onChange={(e) => setStyleDraft({...styleDraft, font: e.target.value})}>
                                        <option value="Inter">Inter</option>
                                        <option value="Roboto">Roboto</option>
                                        <option value="Open Sans">Open Sans</option>
                                        <option value="Lato">Lato</option>
                                        <option value="Montserrat">Montserrat</option>
                                        <option value="Playfair Display">Playfair Display</option>
                                        <option value="Courier New">Courier New</option>
                                    </select>
                                </div>
                                <div style={{display:'grid', gridTemplateColumns: '1fr 1fr', gap: 20}}>
                                    <div><label style={{color: '#8F9EB5', display: 'block'}}>Accent Color</label><input type="color" value={styleDraft.accentColor} onChange={(e)=>setStyleDraft({...styleDraft, accentColor: e.target.value})} style={{height: 40, width: 60, border:'none', background:'transparent', cursor:'pointer'}} /></div>
                                    <div><label style={{color: '#8F9EB5', display: 'block'}}>Sidebar</label><input type="color" value={styleDraft.sidebarBg} onChange={(e)=>setStyleDraft({...styleDraft, sidebarBg: e.target.value})} style={{height: 40, width: 60, border:'none', background:'transparent', cursor:'pointer'}} /></div>
                                    <div><label style={{color: '#8F9EB5', display: 'block'}}>BG Start</label><input type="color" value={styleDraft.bgStart} onChange={(e)=>setStyleDraft({...styleDraft, bgStart: e.target.value})} style={{height: 40, width: 60, border:'none', background:'transparent', cursor:'pointer'}} /></div>
                                    <div><label style={{color: '#8F9EB5', display: 'block'}}>BG End</label><input type="color" value={styleDraft.bgEnd} onChange={(e)=>setStyleDraft({...styleDraft, bgEnd: e.target.value})} style={{height: 40, width: 60, border:'none', background:'transparent', cursor:'pointer'}} /></div>
                                </div>
                                <div style={{marginTop: 30}}>
                                    <button className="PrimaryButton" onClick={() => saveWrapper(saveGlobalStyle, styleDraft, "Style Saved")}>Save & Apply Styles</button>
                                    <button style={{marginLeft: 10, background:'transparent', border:'1px solid #555', color:'#ccc', padding:'12px 20px', borderRadius:12, cursor:'pointer'}} onClick={() => {if(window.confirm("Reset?")) saveWrapper(saveGlobalStyle, { font: 'Inter', accentColor: '#6C5DD3', bgStart: '#7e6cd9', bgEnd: '#4a32e1', sidebarBg: '#110e22', cardBg: 'rgba(20, 20, 35, 0.6)' }, "Reset Complete");}}>Reset Defaults</button>
                                </div>
                            </div>
                        )} */}
                    </div>
                </div>
            </div>
        </div>
    </div>
  );
};

export default Admin;
